import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Alert,
  IconButton, Tooltip, Select, MenuItem, FormControl,
  InputLabel, LinearProgress, Switch, FormControlLabel
} from '@mui/material';
import RefreshIcon     from '@mui/icons-material/Refresh';
import TerminalIcon    from '@mui/icons-material/Terminal';
import { sensorsAPI }  from '../services/api';

const LEVEL_COLORS = {
  INFO:  { bg: '#E3F2FD', color: '#1F4E79', border: '#90CAF9' },
  WARN:  { bg: '#FFF3E0', color: '#E65100', border: '#FFB74D' },
  ERROR: { bg: '#FFEBEE', color: '#C62828', border: '#EF9A9A' },
};

function LogRow({ log }) {
  const c    = LEVEL_COLORS[log.level] || LEVEL_COLORS.INFO;
  const time = new Date(log.received_at).toLocaleTimeString('fr-FR', { hour12: false });
  const date = new Date(log.received_at).toLocaleDateString('fr-FR');
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.8, px: 1.5,
      borderBottom: '1px solid #F0F0F0',
      '&:hover': { bgcolor: '#FAFAFA' },
    }}>
      <Chip label={log.level} size="small" sx={{
        bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`,
        fontWeight: 700, fontSize: '0.68rem', minWidth: 48, height: 20, mt: 0.2,
      }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.85rem', color: '#212121', wordBreak: 'break-word' }}>
          {log.msg}
        </Typography>
        {log.ts_ms != null && (
          <Typography sx={{ fontSize: '0.7rem', color: '#9E9E9E' }}>
            uptime {Math.floor(log.ts_ms / 1000)}s
          </Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#1F4E79', fontWeight: 600 }}>
          {time}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#9E9E9E' }}>{date}</Typography>
      </Box>
    </Box>
  );
}

export default function Logs() {
  const [sensors,     setSensors]     = useState([]);
  const [sensorId,    setSensorId]    = useState('');
  const [logs,        setLogs]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate,  setLastUpdate]  = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    sensorsAPI.status()
      .then(r => {
        const s = r.data.sensors || [];
        setSensors(s);
        if (s.length > 0) setSensorId(s[0].sensor_id);
      })
      .catch(() => {});
  }, []);

  const loadLogs = () => {
    if (!sensorId) return;
    setLoading(true);
    sensorsAPI.logs(sensorId)
      .then(r => {
        setLogs(r.data.logs || []);
        setLastUpdate(new Date().toLocaleTimeString('fr-FR'));
        setError('');
      })
      .catch(() => setError('Impossible de charger les logs'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadLogs(); }, [sensorId]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh && sensorId) {
      intervalRef.current = setInterval(loadLogs, 15000);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, sensorId]);

  const levelCounts = logs.reduce((acc, l) => {
    acc[l.level] = (acc[l.level] || 0) + 1; return acc;
  }, {});

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1F4E79">Logs capteurs</Typography>
          <Typography variant="body2" color="text.secondary">
            {lastUpdate ? `Mis a jour : ${lastUpdate}` : 'Chargement...'}
            {autoRefresh && ' · auto-refresh 15s'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch checked={autoRefresh} size="small"
                onChange={e => setAutoRefresh(e.target.checked)} />
            }
            label={<Typography variant="body2">Auto</Typography>}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Capteur</InputLabel>
            <Select value={sensorId} label="Capteur"
              onChange={e => setSensorId(e.target.value)}>
              {sensors.map(s => (
                <MenuItem key={s.sensor_id} value={s.sensor_id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                      bgcolor: s.is_online ? '#4CAF50' : '#F44336' }} />
                    {s.sensor_id}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Actualiser">
            <IconButton onClick={loadLogs} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        {Object.entries(LEVEL_COLORS).map(([lvl, c]) => (
          <Chip key={lvl} label={`${lvl} : ${levelCounts[lvl] || 0}`}
            size="small" sx={{ bgcolor: c.bg, color: c.color,
              border: `1px solid ${c.border}`, fontWeight: 600 }} />
        ))}
        <Chip label={`Total : ${logs.length}`} size="small"
          sx={{ bgcolor: '#F5F5F5', color: '#757575', fontWeight: 600 }} />
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5,
            bgcolor: '#1F4E79', borderRadius: '12px 12px 0 0' }}>
            <TerminalIcon sx={{ color: 'white', fontSize: 18 }} />
            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem', flex: 1 }}>
              {sensorId || 'Selectionner un capteur'}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>
              {logs.length} entrees
            </Typography>
          </Box>
          <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
            {logs.length === 0 && !loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary" fontSize="0.9rem">
                  {sensorId ? 'Aucun log disponible.' : 'Selectionnez un capteur.'}
                </Typography>
                <Typography color="text.secondary" fontSize="0.8rem" mt={0.5}>
                  Les logs arrivent avec chaque fenetre de 5 min (firmware v1.0.9+)
                </Typography>
              </Box>
            ) : (
              logs.map((log, i) => <LogRow key={i} log={log} />)
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
